import { useEffect, useState } from 'react';

const STORAGE_KEY = 'blogforge:inspector-width';

/**
 * Width + collapse state for the inspector panel, persisted to localStorage.
 * The panel is the right-hand grid column; drag the handle on its left edge
 * to resize (dragging left widens the panel).
 */
export function useResizablePanel(defaultWidth = 300, min = 240, max = 640) {
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) && stored >= min && stored <= max ? stored : defaultWidth;
  });
  // Small screens start with the inspector collapsed — it opens as an
  // overlay sheet from the toolbar's style button.
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(Math.round(width)));
  }, [width]);

  const startResize = (e: { clientX: number; preventDefault: () => void }) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: PointerEvent) => {
      // Handle sits on the panel's left edge: dragging left widens it.
      const next = startWidth + (startX - ev.clientX);
      setWidth(Math.min(max, Math.max(min, next)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return {
    width,
    collapsed,
    setCollapsed,
    toggleCollapsed: () => setCollapsed((c) => !c),
    startResize,
  };
}
